import { Directive, ElementRef, HostListener, inject } from '@angular/core';

/**
 * Directive that restricts input to numbers only
 * Prevents non-numeric characters from being entered via typing, pasting, or other input methods
 */
@Directive({
  selector: '[appNumberOnly]',
  standalone: true,
})
export class NumberOnlyDirective {
  private readonly el = inject(ElementRef);

  // Constants for better maintainability
  private static readonly ALLOWED_KEYS = [
    'Backspace',
    'Delete',
    'Tab',
    'Escape',
    'Enter',
    'ArrowLeft',
    'ArrowUp',
    'ArrowRight',
    'ArrowDown',
  ]; // navigation keys
  private static readonly NUMERIC_KEYS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9']; // 0-9 keys
  private static readonly NON_NUMERIC_REGEX = /\D/g;

  /**
   * Handles input changes and filters out non-numeric characters
   * @param event - The input event
   */
  @HostListener('input', ['$event'])
  onInputChange(event: InputEvent): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    // Remove any non-numeric characters
    const numericValue = value.replace(NumberOnlyDirective.NON_NUMERIC_REGEX, '');

    // Update input value if it changed
    if (value !== numericValue) {
      input.value = numericValue;
      // Trigger input event to update Angular's form control
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  /**
   * Handles key press events and prevents non-numeric keys
   * @param event - The keyboard event
   */
  @HostListener('keypress', ['$event'])
  onKeyPress(event: KeyboardEvent): void {
    // Allow: backspace, delete, tab, escape, enter, and navigation keys
    if (NumberOnlyDirective.ALLOWED_KEYS.includes(event.key)) {
      return;
    }

    // Allow only numbers
    if (NumberOnlyDirective.NUMERIC_KEYS.includes(event.key)) {
      return;
    }

    // Prevent the default action for all other keys
    event.preventDefault();
  }

  /**
   * Handles paste events and filters out non-numeric characters
   * @param event - The clipboard event
   */
  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') || '';
    const numericText = pastedText.replace(NumberOnlyDirective.NON_NUMERIC_REGEX, '');

    // Insert only numeric characters
    const input = event.target as HTMLInputElement;
    input.value = numericText;
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }
}
